import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Copy, CheckCircle2, Globe, RefreshCw, Plus, Save } from "lucide-react";

const STATUS_CLASS = {
  verified: "bg-emerald-600 text-white",
  pending: "bg-amber-500 text-white",
  not_started: "bg-slate-500 text-white",
  failed: "bg-red-600 text-white",
  temporary_failure: "bg-red-500 text-white",
};

/** Admin: real domains in the Resend account + exact DNS records + verify + sender to auto-activate. */
export function ResendDomains({ t, onSenderChanged }) {
  const [domains, setDomains] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newDomain, setNewDomain] = useState("");
  const [pending, setPending] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const loadDomains = async () => {
    const { data } = await api.get("/resend/domains");
    setDomains(data.domains || []);
    if (!selected && data.domains?.length) {
      const first = data.domains.find((d) => d.status !== "verified") || data.domains[0];
      setSelected(first.id);
    }
  };
  useEffect(() => {
    loadDomains().catch(() => {});
    api.get("/resend/pending-sender").then((r) => setPending(r.data.pending_sender || "")).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!selected) return;
    api.get(`/resend/domains/${selected}`).then((r) => setDetail(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [selected]);

  const addDomain = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/resend/domains", { name: newDomain });
      toast.success(`${t("set.domainAdded")}: ${data.name}`);
      setNewDomain("");
      await loadDomains();
      setSelected(data.id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/resend/domains/${selected}/verify`);
      setDetail(data);
      await loadDomains();
      if (data.status === "verified") { toast.success(t("set.domainVerified")); onSenderChanged?.(); }
      else toast.info(`${t("set.domainStatus")}: ${data.status}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const savePending = async () => {
    try {
      const { data } = await api.put("/resend/pending-sender", { sender_email: pending });
      toast.success(data.applied ? t("set.pendingApplied") : t("set.pendingSaved"));
      if (data.applied) onSenderChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); }
    catch { toast.error("Clipboard not available"); }
  };

  return (
    <div className="border p-3 mb-5 bg-muted/20" data-testid="resend-domains-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1"><Globe size={12} /> {t("set.domains")}</div>
        <div className="flex gap-2">
          <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="mafrissa.com"
            className="rounded-none h-8 text-xs w-44" data-testid="resend-new-domain-input" />
          <Button size="sm" className="rounded-none text-xs h-8" disabled={busy || !newDomain.includes(".")} onClick={addDomain} data-testid="resend-add-domain-btn">
            <Plus size={12} className="mr-1" /> {t("set.addDomain")}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mb-3" data-testid="resend-domain-list">
        {domains.map((d) => (
          <button key={d.id} type="button" onClick={() => setSelected(d.id)} data-testid={`resend-domain-tab-${d.name}`}
            className={`border px-2 py-1 text-[11px] font-bold flex items-center gap-2 ${selected === d.id ? "border-primary bg-primary/10" : "border-border"}`}>
            {d.name}
            <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${STATUS_CLASS[d.status] || "bg-slate-400 text-white"}`}>{d.status}</span>
          </button>
        ))}
        {domains.length === 0 && <span className="text-[11px] text-muted-foreground">{t("set.noDomains")}</span>}
      </div>

      {detail && (
        <div data-testid="resend-domain-detail">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-xs">
              <span className="font-black">{detail.name}</span> · {detail.region} ·{" "}
              <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${STATUS_CLASS[detail.status] || ""}`} data-testid="resend-domain-detail-status">{detail.status}</span>
            </div>
            <Button size="sm" variant="outline" className="rounded-none text-xs h-8" disabled={busy || detail.status === "verified"} onClick={verify} data-testid="resend-verify-domain-btn">
              <RefreshCw size={12} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> {t("set.verifyNow")}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mb-2">{t("set.dnsExactHint")}</p>
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>{t("set.recordType")}</th><th>{t("set.host")}</th><th>{t("set.value")}</th><th>Prio</th><th>{t("common.status")}</th><th></th></tr></thead>
            <tbody>
              {detail.records.map((r, i) => (
                <tr key={i} data-testid={`resend-dns-row-${i}`}>
                  <td className="font-black text-xs">{r.type} <span className="text-[9px] text-muted-foreground font-normal">{r.record}</span></td>
                  <td className="font-mono text-[11px]">
                    <div className="flex items-center gap-1">{r.name}
                      <button onClick={() => copy(r.name, `h${i}`)} className="text-muted-foreground hover:text-primary" data-testid={`resend-copy-host-${i}`}>
                        {copied === `h${i}` ? <CheckCircle2 size={11} className="text-emerald-600" /> : <Copy size={11} />}</button>
                    </div>
                  </td>
                  <td className="font-mono text-[10px] break-all max-w-md">
                    <div className="flex items-start gap-1"><span>{r.value}</span>
                      <button onClick={() => copy(r.value, `v${i}`)} className="text-muted-foreground hover:text-primary flex-shrink-0" data-testid={`resend-copy-value-${i}`}>
                        {copied === `v${i}` ? <CheckCircle2 size={11} className="text-emerald-600" /> : <Copy size={11} />}</button>
                    </div>
                  </td>
                  <td className="tabular-nums text-xs">{r.priority ?? "—"}</td>
                  <td><span className={`px-1.5 py-0.5 text-[9px] font-black uppercase ${STATUS_CLASS[r.status] || "bg-slate-400 text-white"}`}>{r.status}</span></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      <div className="mt-4 pt-3 border-t grid md:grid-cols-[1fr_auto] gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.pendingSender")}</Label>
          <Input value={pending} onChange={(e) => setPending(e.target.value)} placeholder="compras@mafrissa.com"
            className="rounded-none h-8 text-xs" data-testid="resend-pending-sender-input" />
          <p className="text-[9px] text-muted-foreground">{t("set.pendingSenderHint")}</p>
        </div>
        <Button size="sm" className="rounded-none text-xs h-8" onClick={savePending} data-testid="resend-pending-sender-save">
          <Save size={12} className="mr-1" /> {t("common.save")}
        </Button>
      </div>
    </div>
  );
}
